import React from 'react';

import { Skeleton } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { v4 as uuid } from 'uuid';

import { IButtonImageV3 } from '../../../../interfaces/button';
import { TRANSLATIONS_GLOBAL } from '../../../../translations/commons';
import { GenericFunction } from '../../../../types/genericTypes';
import BdsIconButton from '../../Buttons/BdsIconButton/BdsIconButton';
import ErrorCard from '../ErrorCard/ErrorCard';

import './StepsCard.scss';

export interface IStepsCard {
  title: string;
  iconButton?: IButtonImageV3;
  steps: (string | React.ReactElement)[];
  action?: GenericFunction;
  loading?: boolean;
  error?: boolean;
  retry?: GenericFunction;
}

const StepsCard = ({ title, iconButton, steps, action, loading = false, error = false, retry }: IStepsCard) => {
  const { t } = useTranslation();

  if (loading) {
    return <Skeleton className="steps-skeleton" height={350} variant="rectangular" />;
  }

  if (error) {
    return (
      <div className="steps-card-container flex flex-col gap-8 p-10" data-experience="new">
        <ErrorCard
          primaryText={t(TRANSLATIONS_GLOBAL.errorCard.title)}
          secondaryText={t(TRANSLATIONS_GLOBAL.errorCard.description)}
          action={retry}
          buttonText={t(TRANSLATIONS_GLOBAL.errorCard.buttonText)}
        />
      </div>
    );
  }

  return (
    <>
      {steps.length > 0 && (
        <div className="steps-card-container flex flex-col gap-8 p-10" data-experience="new">
          <div className="flex flex-row gap-6 items-center justify-between">
            <div className="steps-title">{title}</div>
            {iconButton && action && <BdsIconButton icon={iconButton} action={action} variant="primary" size="sm" />}
          </div>
          <ul className="steps-list flex flex-col p-0 m-0 gap-1">
            {steps.map((step, index) => (
              <li key={`step-${uuid()}`} className="step-item flex flex-row gap-4 items-center">
                <div className="step-position flex justify-center h-8 w-8">{index + 1}</div>
                <div className="step-text">{step}</div>
              </li>
            ))}
          </ul>
        </div>
      )}
    </>
  );
};

export default StepsCard;
